// What a layer of the WebGL2 client needs from the engine, stated as three
// small structural interfaces (R431).
//
// A layer writes instances into a batch and never touches WebGL. The
// engine's `QuadBatch`, `SegmentBatch` and `CircleBatch` satisfy these
// shapes, and so does a plain recording object — which is how a layer is
// tested in jsdom, where there is no GL context at all.
//
// The interfaces are structural on purpose: nothing here imports `gl/`, so
// a layer module carries no path into the context, the programs or the
// atlas rasteriser.

import type { CameraUniforms } from '../layer';

/** One packed mark of the atlas, as `gfx/atlas.ts` records it. */
export interface SpriteEntry {
  key: string;
  u0: number; v0: number; u1: number; v1: number;
  /** The cropped box in world px at scale 1. */
  w: number; h: number;
  /** Where the box centre sits against the drawing origin, in world px. */
  ox: number; oy: number;
  /**
   * A glyph cell's advance in px at scale 1, which the pen adds after it
   * (R548). A mark that is not a glyph has none.
   */
  adv?: number;
  /** A glyph cell's baseline, in px under the cell TOP at scale 1. */
  base?: number;
}

/** Instanced quads: sprites, bars, frames and flat rectangles. */
export interface QuadSink {
  begin(): void;
  readonly count: number;
  /**
   * A fade over everything the batch draws, written as one uniform on
   * `flush`. 1 is the shipping pixel; a layer that dims its whole output
   * sets this instead of scaling the alpha of every instance it packs.
   */
  globalAlpha: number;
  /**
   * The band scale of the four chart text steps (R1796), written as one
   * uniform on `flush`: a quad whose flags carry a step is scaled about its
   * pivot by its entry. 1 everywhere is the shipping pixel.
   */
  readonly stepScale: Float32Array;
  quad(
    x: number, y: number, ox: number, oy: number, w: number, h: number,
    rot: number, flags: number, color: number, alpha: number,
    u0: number, v0: number, u1: number, v1: number,
  ): void;
  sprite(
    x: number, y: number, rot: number, entry: SpriteEntry,
    tint: number, alpha: number, scale?: number, flags?: number, centred?: boolean,
  ): void;
  /**
   * One glyph of a chart word (R1796): a centred sprite whose box stands at
   * `x, y` and whose offset is kept from the pivot `px, py`, so the band
   * scale grows or shrinks the whole word about that one point.
   */
  glyph(
    px: number, py: number, x: number, y: number, rot: number, entry: SpriteEntry,
    tint: number, alpha: number, scale: number, flags: number,
  ): void;
  flush(cam: CameraUniforms): number;
}

/** Instanced capsules: every straight line, dashed or solid. */
export interface SegmentSink {
  begin(): void;
  readonly count: number;
  /**
   * A fade over everything the batch draws, written as one uniform on
   * `flush`. 1 is the shipping pixel; a layer that dims its whole output
   * sets this instead of scaling the alpha of every instance it packs.
   */
  globalAlpha: number;
  line(
    x0: number, y0: number, x1: number, y1: number,
    width: number, color: number, alpha: number,
    flags?: number, dashOn?: number, dashOff?: number,
  ): void;
  flush(cam: CameraUniforms): number;
}

/** Instanced SDF circles: rings, discs, arcs and dashed haloes. */
export interface CircleSink {
  begin(): void;
  readonly count: number;
  /**
   * A fade over everything the batch draws, written as one uniform on
   * `flush`. 1 is the shipping pixel; a layer that dims its whole output
   * sets this instead of scaling the alpha of every instance it packs.
   */
  globalAlpha: number;
  ring(x: number, y: number, r: number, width: number, color: number, alpha: number, flags?: number): void;
  disc(x: number, y: number, r: number, color: number, alpha: number, flags?: number): void;
  arc(
    x: number, y: number, r: number, width: number,
    start: number, sweep: number, color: number, alpha: number, flags?: number,
  ): void;
  dashed(
    x: number, y: number, r: number, width: number,
    count: number, fill: number, color: number, alpha: number,
    start?: number, sweep?: number, flags?: number,
  ): void;
  flush(cam: CameraUniforms): number;
}

/**
 * A paint sheet (R2657): one 2D canvas a layer paints a world rectangle
 * into, one texture and one quad. It carries the looks that paint each
 * frame on a plain 2D context (`render/seaobject-kit.ts`), which no batch
 * can state: a clip, a gradient, a filled path and a word.
 *
 * A layer paints only when its picture changed: `begin` hands it the
 * context with the world transform set, the layer paints, and `end`
 * uploads. Between two paints the quad stands where it was painted, so a
 * pan costs nothing. `begin` answers null where there is no 2D canvas (a
 * recorder of the tests, an engine stub), and the layer then paints
 * nothing.
 */
export interface SheetSink {
  /** Paints the world rect `x, y, w, h` at `scale` device px per world px. */
  begin(x: number, y: number, w: number, h: number, scale: number): CanvasRenderingContext2D | null;
  /** Uploads what the context painted since `begin`. */
  end(): void;
  /** False hides the quad; the painted picture stays for the next show. */
  shown: boolean;
  /** Draws the quad, and answers the draw count. */
  flush(cam: CameraUniforms): number;
}

/**
 * What a layer asks for its batches and its marks. `Engine` satisfies it,
 * and a test hands in a recorder instead.
 */
export interface BatchSource {
  newQuads(capacity?: number): QuadSink;
  newSegments(capacity?: number): SegmentSink;
  newCircles(capacity?: number): CircleSink;
  /** An atlas entry, baked on demand for a key the atlas does not hold. */
  sprite(key: string): SpriteEntry;
  /** A paint sheet (R2657). A source with none hands a layer
   *  `sheetOf` hands a layer a sheet that paints nothing. */
  newSheet?(): SheetSink;
}

/** The sheet of a source, or one that paints and draws nothing where the
 *  source has none. */
export function sheetOf(src: BatchSource): SheetSink {
  return src.newSheet?.() ?? { begin: () => null, end: () => {}, shown: false, flush: () => 0 };
}
